// src/com/veh_type_info/VehTypeInfo.js
import React, { useEffect, useRef, useState } from 'react';
// import { styled } from '@mui/material/styles';
import { CircularProgress, IconButton } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PageviewIcon from '@mui/icons-material/Pageview';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { vehTypeInfoListRepo, authTableTreeRepo } from '../../value/AppModel';
import {
    LIST_VEH_TYPE_INFO, GET_VEH_TYPE_INFO,
    ADD_VEH_TYPE_INFO, EDIT_VEH_TYPE_INFO, REMOVE_VEH_TYPE_INFO
} from './VehTypeInfoGql';
import { VehTypeInfoDictionary } from './VehTypeInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import VehTypeInfoView from './VehTypeInfoView';
import VehTypeInfoInput from './VehTypeInfoInput';

/**
 * 차종정보 관리.
 * @param {*} param0 
 * @returns 
 */
export default function VehTypeInfo({
    tabsForSwitchingView // for switching view
}) {
    const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const vehTypeInfoListData = useReactiveVar(vehTypeInfoListRepo);
    const [searchText, setSearchText] = useState('');
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getVehTypeInfoList, responseList] = useLazyQuery(LIST_VEH_TYPE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehTypeInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getVehTypeInfoItem, responseItemToEdit] = useLazyQuery(GET_VEH_TYPE_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: VehTypeInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addVehTypeInfo, responseAdd] = useMutation( ADD_VEH_TYPE_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: VehTypeInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editVehTypeInfo, responseEdit] = useMutation( EDIT_VEH_TYPE_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: VehTypeInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeVehTypeInfo, responseRemove] = useMutation( REMOVE_VEH_TYPE_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: VehTypeInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getVehTypeInfoListNow = () => {
        getVehTypeInfoList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - vehTypeInfoListData.when;
        if(diff > 300)
            getVehTypeInfoListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehTypeInfoList) {
            vehTypeInfoListRepo({when:new Date().getTime()/1000, records:data.vehTypeInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.vehTypeInfoItem) {
            if(isEditingRef.current) setEditorState({open:true, item:data.vehTypeInfoItem});
            else setViewerState({open:true, item:data.vehTypeInfoItem});
        }
        else addAlert({title:VehTypeInfoDictionary.errorTitle.Get, message:['차종정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.vehTypeInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehTypeInfoListNow();
        }
        else addAlert({title:VehTypeInfoDictionary.errorTitle.Add, message:[data.vehTypeInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.vehTypeInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehTypeInfoListNow();
        }
        else addAlert({title:VehTypeInfoDictionary.errorTitle.Edit, message:[data.vehTypeInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.vehTypeInfoRemove.ok) getVehTypeInfoListNow();
        else addAlert({title:VehTypeInfoDictionary.errorTitle.Remove, message:[data.vehTypeInfoRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getVehTypeInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{vehTypeInfo:item}};
        if(isEdit) editVehTypeInfo(mutateParam);
        else addVehTypeInfo(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getVehTypeInfoItem({variables:{vehTypeId:item.vehTypeId}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "차종정보 삭제",
            content: [
                `차종 '${item.vehTypeName}' 을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm) removeVehTypeInfo({variables:{vehTypeInfo:{vehTypeId:item.vehTypeId}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getVehTypeInfoItem({variables:{vehTypeId:record.vehTypeId}});
    };

    const onCloseView = () => {
        setViewerState(AppObject.EmptyViewerState);
    };

    const onRequestEditFromViewer = (item) => {
        setViewerState(AppObject.EmptyViewerState);
        setEditorState({open: true, item: item});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.VIEW: onDoubleClickRow(row); break;
            default: return;
        }
    };
    /*
	CALL wps_set_auth_table('pr_veh_type_info_add', '1,2', v_msg);
	CALL wps_set_auth_table('pr_veh_type_info_edit', '1,2', v_msg);
	CALL wps_set_auth_table('pr_veh_type_info_remove', '1,2', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_veh_type_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_veh_type_info_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_veh_type_info_remove);

    const tools = [
        {id:AppObject.command.VIEW, icon:<PageviewIcon/> }
    ];
    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseList.loading
    || responseItemToEdit.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    return(
        <ContentConainer>
            {tabsForSwitchingView}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={24} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={VehTypeInfoDictionary.tableProps.tableId}
                tableProps={VehTypeInfoDictionary.tableProps}
                records={vehTypeInfoListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
            />
            <VehTypeInfoView
                open={viewerState.open}
                item={viewerState.item}
                onRequestEdit={onRequestEditFromViewer}
                onClose={onCloseView}
            />
            <VehTypeInfoInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
